import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { Repeat2 } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useAuthors } from "@/hooks/platform/useAuthors";
import { mediaUrls, isVideoUrl } from "@/lib/mediaUrl";
import AuthorChip from "@/components/platform/AuthorChip";
import { Button } from "@/components/ui/button";
import MediaImage from "@/components/platform/MediaImage";
import { useMediaUrl } from "@/lib/storageUrl";

const RepostVideo = ({ url }: { url: string }) => {
  const resolved = useMediaUrl(url);
  return <video src={resolved ?? undefined} controls className="max-h-64 w-full rounded object-cover" />;
};

const RepostEmbed = ({ sourceId }: { sourceId: string }) => {
  const { data: original, isLoading } = useQuery({
    queryKey: ["repost-source", sourceId],
    queryFn: async () => {
      const { data, error } = await supabase.from("posts").select("*").eq("id", sourceId).eq("visibility", "public").maybeSingle();
      if (error) throw error;
      return data;
    },
  });
  const { getAuthor } = useAuthors(original ? [original.author_id] : []);
  if (isLoading) return <div className="rounded border border-border p-4 text-sm text-muted-foreground">Loading original post…</div>;
  if (!original) return <div className="rounded border border-border p-4 text-sm text-muted-foreground">Original post is unavailable.</div>;
  const media = mediaUrls(original.media);
  return <div className="space-y-3 rounded border border-border bg-background p-3">
    <div className="flex items-center gap-1 text-xs text-muted-foreground"><Repeat2 className="h-3.5 w-3.5" /> Original post</div>
    <AuthorChip author={getAuthor(original.author_id)} size="sm" />
    <p className="whitespace-pre-wrap text-sm text-foreground">{original.content}</p>
    {media[0] && (isVideoUrl(media[0]) ? <RepostVideo url={media[0]} /> : <MediaImage src={media[0]} alt="Original post media" className="max-h-64 w-full rounded object-cover" loading="lazy" />)}
    <Button asChild variant="link" size="sm" className="px-0"><Link to={`/post/${sourceId}`}>View original</Link></Button>
  </div>;
};

export default RepostEmbed;